import Badge from '@cloudscape-design/components/badge';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useTranslate } from 'src/i18n/useTranslate';
import { isWithinUpdatedRecentlyWindow } from './constants';
import { TopicChanges } from './types';
import { useTopicChanges } from './useTopicChanges';

// Extend dayjs once per module load; the plugin is idempotent so this is
// safe to re-import from multiple components (TopicChangesBadge +
// WhatsChangedPanel both use relative time). Placed AFTER all imports
// to satisfy the import/first lint rule.
dayjs.extend(relativeTime);

type TopicChangesBadgeProps = {
    documentId: string;
    lcmsXmlGUID: string;
    /**
     * Explicit ownership flag. When true the badge treats the parent
     * as the sole owner of the topic-changes fetch and never fires its
     * own useApi call. Making this explicit rather than inferring it
     * from the three `topicChanges*` props below avoids a race on the
     * first render: on mount, a parent that owns the fetch typically
     * passes `topicChanges=undefined`, `topicChangesLoading=false`,
     * `topicChangesIs404=false` because its loading state has not
     * flipped yet, which would otherwise fail an "any of these is set"
     * heuristic and let the badge fire a duplicate request before the
     * parent's next commit.
     */
    parentManaged?: boolean;
    /**
     * Parent-provided topic-changes payload. The badge still applies
     * the Updated_Recently_Window gate on `latestChangeAt` locally so
     * that the 30-day scope is enforced exactly once by the component
     * that cares about it.
     */
    topicChanges?: TopicChanges;
    /**
     * True while the parent-owned fetch is in flight. The badge hides
     * itself during loading.
     */
    topicChangesLoading?: boolean;
    /**
     * True when the parent-owned fetch returned 404 ("no topic-change
     * record"). The badge hides itself.
     */
    topicChangesIs404?: boolean;
};

/**
 * Renders a Cloudscape Badge on the topic header when the topic has been
 * updated within the Updated_Recently_Window (30 days). Hidden otherwise
 * — this includes the 404 / loading / no-data / stale-timestamp branches.
 *
 * Requirements 3.1-3.4.
 */
export const TopicChangesBadge = ({
    documentId,
    lcmsXmlGUID,
    parentManaged = false,
    topicChanges,
    topicChangesLoading = false,
    topicChangesIs404 = false,
}: TopicChangesBadgeProps) => {
    const t = useTranslate();
    // Suppress the badge's own fetch when the parent is declared as the
    // owner. Passing empty identifiers to `useTopicChanges` short-circuits
    // its useEffect, so `fetched` stays idle for the lifetime of the
    // mount. This keeps the "share one HTTP call across badge + panel"
    // guarantee from Requirement 3.0 intact even on the first render,
    // when `topicChangesLoading` / `topicChangesIs404` have not yet
    // flipped to their real values.
    const fetched = useTopicChanges(
        parentManaged ? '' : documentId,
        parentManaged ? '' : lcmsXmlGUID
    );
    const data = parentManaged ? topicChanges : fetched.data;
    const is404 = parentManaged ? topicChangesIs404 : fetched.is404;
    const loading = parentManaged ? topicChangesLoading : fetched.loading;

    if (loading || is404 || !data) {
        return null;
    }

    if (!isWithinUpdatedRecentlyWindow(data.latestChangeAt)) {
        return null;
    }

    const relative = dayjs(data.latestChangeAt).fromNow();
    const n = data.latestChangedSections.length;
    const label =
        n === 1
            ? t('topic-changes-badge-single', 'section')
            : t('topic-changes-badge-plural', 'sections');

    return (
        <Badge color="green" data-testid="topic-changes-badge">
            {`${t(
                'topic-changes-badge-prefix',
                'Modified'
            )} ${relative} · ${n} ${label}`}
        </Badge>
    );
};
